/**
 * NextAdventureCard — signature dashboard countdown.
 *
 * Renders the user's nearest upcoming trip as a full-bleed premium card.
 * All 5 states (pre_trip / departure_day / on_trip / post_trip / empty)
 * are handled here so the Dashboard host stays clean. Countdown maths
 * runs locally on a 1-minute (or 1-second when <7 days) tick — the
 * backend is only hit once on mount and then every 6 hours for freshness.
 *
 * Architecture note: this card is deliberately state-driven off a single
 * backend payload (`state` + `trip`) so future enhancements (weather,
 * flights, packing progress) can plug straight into the trip object
 * without changing the card's rendering logic.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, Heart, Sparkles, Camera, BookOpen, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, BACKEND_URL } from "@/config/api";

const REFRESH_MS = 6 * 60 * 60 * 1000; // background refresh every 6h

function useCountdown(startDateIso, isLive) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startDateIso) return;
    // 1s tick when <7 days remain (isLive), otherwise 1min
    const interval = isLive ? 1000 : 60_000;
    const id = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(id);
  }, [startDateIso, isLive]);

  return useMemo(() => {
    if (!startDateIso) return null;
    const start = new Date(startDateIso).getTime();
    const diffMs = start - now;
    if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, past: true };
    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return { days, hours, minutes, seconds, past: false };
  }, [startDateIso, now]);
}

function formatRange(start, end) {
  if (!start) return "";
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const fmt = (d, opts) => d.toLocaleDateString("en-GB", opts);
  if (!e || s.toDateString() === e.toDateString()) {
    return fmt(s, { day: "numeric", month: "long", year: "numeric" });
  }
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${s.getDate()}–${e.getDate()} ${fmt(s, { month: "long", year: "numeric" })}`;
  }
  return `${fmt(s, { day: "numeric", month: "short" })} – ${fmt(e, { day: "numeric", month: "short", year: "numeric" })}`;
}

const flagEmoji = (cc) => {
  if (!cc) return "";
  return cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
};

export default function NextAdventureCard({ onPlanTrip, refreshKey = 0 }) {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);

  const fetchNext = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/next-adventure`);
      if (alive.current) setPayload(res.data);
    } catch (_) {
      /* silent — dashboard shouldn't break if this endpoint hiccups */
    } finally {
      if (alive.current) setLoading(false);
    }
  };

  useEffect(() => {
    alive.current = true;
    fetchNext();
    const id = setInterval(fetchNext, REFRESH_MS);
    // Refresh when tab becomes visible again — catches "user came back next day"
    const onVis = () => { if (document.visibilityState === "visible") fetchNext(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive.current = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Refetch when parent signals a trip mutation (add/edit/delete/toggle).
  // This catches the case where a user adds dates to an existing dateless
  // trip — without this the card stays in the "empty" state until reload.
  useEffect(() => {
    if (refreshKey === 0) return; // skip initial mount (handled above)
    fetchNext();
  }, [refreshKey]);

  const trip = payload?.trip;
  const state = payload?.state || (loading ? "loading" : "empty");
  const isLive = trip?.days_until != null && trip.days_until <= 7 && trip.days_until > 0;
  const countdown = useCountdown(trip?.start_date, isLive);

  if (loading) {
    return <div className="h-56 rounded-2xl bg-muted/30 animate-pulse mb-6" data-testid="next-adventure-loading" />;
  }

  // ── EMPTY ─────────────────────────────────────────────────────────────
  if (state === "empty") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="mb-6 rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-sky-500/10 via-indigo-500/5 to-purple-500/10 p-8 text-center"
        data-testid="next-adventure-empty"
      >
        <Plane className="w-10 h-10 mx-auto text-primary mb-3" strokeWidth={1.5} />
        <h3 className="font-serif text-xl text-foreground mb-2">Your next adventure starts here.</h3>
        <p className="text-sm text-muted-foreground mb-5 max-w-xs mx-auto">
          Start planning your next trip together and we&apos;ll count down the days.
        </p>
        <Button
          onClick={() => {
            if (typeof onPlanTrip === "function") {
              onPlanTrip();
              return;
            }
            // Fallback: try the Dashboard FAB, then the header add button
            const fab =
              document.querySelector("[data-testid='add-item-fab']") ||
              document.querySelector("[data-testid='header-add-item-btn']");
            fab?.click();
          }}
          className="bg-primary text-white hover:bg-primary/90 rounded-full h-10 px-6"
          data-testid="next-adventure-plan-btn"
        >
          Plan a Trip
        </Button>
      </motion.div>
    );
  }

  // ── LOADED (pre / departure / on / post) ─────────────────────────────
  const bgSrc = trip.cover_photo_url ? `${BACKEND_URL}${trip.cover_photo_url}` : null;
  const showLiveClock = state === "pre_trip" && isLive && countdown && !countdown.past;

  const handleOpen = () => navigate(`/trip/${trip.id}`);

  return (
    <motion.button
      type="button"
      onClick={handleOpen}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="group relative w-full mb-6 rounded-2xl overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-primary/50 h-56 sm:h-64"
      data-testid="next-adventure-card"
      aria-label={`${trip.is_joint ? "Shared" : "Next"} adventure to ${trip.title || trip.location}, tap to open`}
    >
      {bgSrc ? (
        <img
          src={bgSrc}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600 via-indigo-700 to-purple-800" />
      )}
      {/* Dark gradient overlay — bottom-heavy for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

      <div className="relative h-full flex flex-col justify-between p-5 text-white">
        {/* Header */}
        <div className="flex items-center gap-2">
          {trip.is_joint ? <Heart className="w-4 h-4 text-pink-300 fill-pink-400" /> : <Plane className="w-4 h-4" />}
          <span className="text-xs uppercase tracking-widest font-medium opacity-90">
            {trip.is_joint ? "Next Adventure Together" : "Next Adventure"}
          </span>
        </div>

        {/* Body */}
        <div>
          <AnimatePresence mode="wait">
            <motion.div
              key={state}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              <p className="text-sm opacity-95 flex items-center gap-1.5">
                {flagEmoji(trip.country_code)} <span className="truncate">{trip.location || trip.title}</span>
              </p>

              {state === "pre_trip" && (
                <>
                  <p className="font-serif text-4xl sm:text-5xl leading-none mt-1" data-testid="countdown-days">
                    {trip.days_until} <span className="text-xl font-sans font-normal opacity-90">Day{trip.days_until === 1 ? "" : "s"}</span>
                  </p>
                  {showLiveClock && (
                    <p className="text-sm opacity-80 mt-1" data-testid="countdown-live">
                      {countdown.hours}h {countdown.minutes}m {countdown.seconds}s
                    </p>
                  )}
                  <p className="text-xs opacity-70 mt-1">
                    {trip.is_joint && trip.partner_name
                      ? `You and ${trip.partner_name} leave in ${trip.days_until} days.`
                      : "Until your adventure begins"}
                  </p>
                </>
              )}
              {state === "departure_day" && (
                <p className="font-serif text-3xl sm:text-4xl leading-tight mt-1" data-testid="countdown-departure">
                  ✈️ Today&apos;s the day!
                </p>
              )}
              {state === "on_trip" && (
                <p className="font-serif text-3xl sm:text-4xl leading-tight mt-1" data-testid="countdown-ontrip">
                  🌍 Enjoy your adventure!
                </p>
              )}
              {state === "post_trip" && (
                <>
                  <p className="font-serif text-2xl sm:text-3xl leading-tight mt-1" data-testid="countdown-welcome-home">
                    Welcome home!
                  </p>
                  <p className="text-xs opacity-80 mt-1">Ready to relive your adventure?</p>
                </>
              )}

              <p className="text-xs opacity-70 mt-1.5">{formatRange(trip.start_date, trip.end_date)}</p>
            </motion.div>
          </AnimatePresence>

          {/* Progress bar — only for pre_trip */}
          {state === "pre_trip" && trip.total_planning_days && trip.days_until != null && (
            <div className="mt-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80 mb-1">
                <span>Planning</span>
                <div className="flex-1" />
                <span className="inline-flex items-center gap-1">Departure <Plane className="w-3 h-3" /></span>
              </div>
              <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
                <motion.div
                  className="h-full bg-white rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, Math.max(0, ((trip.total_planning_days - trip.days_until) / trip.total_planning_days) * 100))}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
            </div>
          )}

          {/* Post-trip action row */}
          {state === "post_trip" && (
            <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
              <PostTripAction onClick={handleOpen} icon={Camera} label="Upload Memories" testid="post-trip-upload" />
              <PostTripAction onClick={handleOpen} icon={BookOpen} label="Create Scrapbook" testid="post-trip-scrapbook" />
              <PostTripAction
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await axios.put(`${API}/items/${trip.id}`, { completed: true });
                    fetchNext();
                  } catch (_) { /* silent */ }
                }}
                icon={CheckCircle2}
                label="Complete Trip"
                testid="post-trip-complete"
              />
            </div>
          )}
        </div>
      </div>
    </motion.button>
  );
}

function PostTripAction({ onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm hover:bg-white/25 px-3 py-1.5 text-xs font-medium transition-colors"
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
